import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Password } from "../controls";
import { UserService } from "../services";
import { usePersistStore, useStore } from "../store";
import PasswordChecklist from "./PasswordChecklist";
import { Utils } from "../utils";

interface UpdatePasswordModalProps {
  show: boolean;
  onHide: () => void;
}

const UpdatePasswordModal: React.FC<UpdatePasswordModalProps> = ({
  show,
  onHide,
}) => {
  const { setIsLoading, setSuccessMessage } = useStore((s: any) => s);
  const { token, setToken } = usePersistStore((s: any) => s);
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [password1, setPassword1] = useState<string>("");
  const [password2, setPassword2] = useState<string>("");
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [disableButton, setDisableButton] = useState(false);

  const clear = () => {
    setCurrentPassword("");
    setPassword1("");
    setPassword2("");
    setValidationMessage("");
    setDisableButton(false);
  };

  const close = () => {
    clear();
    onHide();
  };

  const handleSubmit = async (e: any) => {
    e?.preventDefault();
    setValidationMessage("");

    if (!currentPassword)
      return setValidationMessage("Please provide your current password.");
    if (!password1)
      return setValidationMessage("Please provide a new password.");
    if (!Utils.checkPasswordStrength(password1).ok)
      return setValidationMessage(
        "New password must be at least 8 characters and include a letter, a number, and a special character."
      );
    if (password1 !== password2)
      return setValidationMessage("New passwords don't match.");

    setDisableButton(true);
    setIsLoading(true);
    const result = await UserService.changePassword(token, {
      currentPassword,
      newPassword: password1,
    });
    if (result?.error) {
      setIsLoading(false);
      setValidationMessage(result?.error);
      setDisableButton(false);
      return;
    }

    if (result?.data?.token) {
      setToken(result.data.token);
    }
    setIsLoading(false);
    setSuccessMessage(
      "Password updated successfully. Use the new password next time you sign in."
    );
    close();
  };

  return (
    <Modal
      className="placeBidModal updatePasswordModal"
      size="lg"
      show={show}
      onHide={close}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton className="placeBidModal__header">
        <Modal.Title className="placeBidModal__heading">
          Update password
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="placeBidModal__body">
        <div className="updatePasswordModal__intro">
          <span className="updatePasswordModal__iconWrap">
            <i className="bi bi-shield-lock" aria-hidden="true" />
          </span>
          <div>
            <h4 className="updatePasswordModal__title">
              Choose a strong new password
            </h4>
            <p className="updatePasswordModal__hint">
              Use at least 8 characters with a mix of letters, numbers, and
              symbols.
            </p>
          </div>
        </div>

        <Password
          label="Current password"
          name="currentPassword"
          placeholder="Enter your current password"
          value={currentPassword}
          onChange={(e: any) => setCurrentPassword(e?.target?.value)}
          required
        />
        <Password
          label="New password"
          name="password1"
          placeholder="At least 8 characters"
          value={password1}
          onChange={(e: any) => setPassword1(e?.target?.value)}
          required
        />
        <PasswordChecklist value={password1} />
        <Password
          label="Confirm new password"
          name="password2"
          placeholder="Re-enter the new password"
          value={password2}
          onChange={(e: any) => setPassword2(e?.target?.value)}
          required
        />

        {validationMessage && (
          <div className="placeBidModal__error">
            <i className="bi bi-x-circle" aria-hidden="true" />
            <span>{validationMessage}</span>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="placeBidModal__footer">
        <button
          type="button"
          className="placeBidModal__cancel"
          onClick={close}
          disabled={disableButton}
        >
          Cancel
        </button>
        <button
          type="button"
          className="placeBidModal__submit"
          onClick={handleSubmit}
          disabled={disableButton}
        >
          <i className="bi bi-check2-circle" aria-hidden="true" />
          <span>Save password</span>
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdatePasswordModal;
